// Web port of the iOS activation-journey hook.
// Source of truth: client-native/src/features/activation/useActivationJourney.ts
// (DO NOT edit the iOS file).
//
// iOS reads stats from a legend-state `globalStore` that is hydrated from the
// `getUserStats` + `getDictionaryStats` endpoints, and reads/writes the journey
// step from server-synced `userSettings.activationStep`. The PWA has no such
// store, so this hook sources the same numbers directly from the existing
// react-query layer:
//
//   - listeningTime / completedLessonsCount / reviewedCardsCount  → getUserStats
//   - knownWordCount / learningWordCount / phrase counts          → getDictionaryStats
//   - feedOpened                                                   → local flag (mirrors
//       iOS MMKV `hasOpenedFeed`; see markFeedOpened below)
//   - activationStep                                               → useUserSettings
//
// The pure step-derivation helpers (deriveCorrectStep / repair) are copied
// verbatim from iOS so the state machine behaves identically.

import { useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { callWithAuth, getUserStats, getDictionaryStats } from "@/api/Api";
import { useUserSettings } from "@/hooks/useUserSettings";
import {
  ACTIVATION_STEPS,
  ACTIVATION_COMPLETE_STEP,
  getStepConfig,
  shouldRepairMissingReviewedCards,
  type ActivationStepConfig,
} from "./activationSteps";
import type { ActivationState } from "./isFeatureUnlocked";

export interface ActivationStats {
  totalWords: number;
  learningWords: number;
  reviewedCards: number;
  knownWords: number;
  phrases: number;
  completedLessons: number;
  listeningTimeSeconds: number;
  feedOpened: boolean;
}

const EMPTY_STATS: ActivationStats = {
  totalWords: 0,
  learningWords: 0,
  reviewedCards: 0,
  knownWords: 0,
  phrases: 0,
  completedLessons: 0,
  listeningTimeSeconds: 0,
  feedOpened: false,
};

// ============================================================================
// Feed-opened flag — mirrors iOS MMKV `hasOpenedFeed` (globalStore.ts).
// The feed track should call `markFeedOpened()` when the user lands on the
// feed so step 6 ("Open the feed") completes.
// ============================================================================

const HAS_OPENED_FEED_KEY = "hasOpenedFeed";

export function hasOpenedFeed(): boolean {
  try {
    return localStorage.getItem(HAS_OPENED_FEED_KEY) === "true";
  } catch {
    return false;
  }
}

export function markFeedOpened(): void {
  try {
    localStorage.setItem(HAS_OPENED_FEED_KEY, "true");
  } catch {
    // Ignore storage failures (private mode / quota) — the worst case is the
    // openedFeed step re-derives as incomplete and the repair pass re-runs.
  }
}

// ============================================================================
// Pure step derivation (ported verbatim from iOS)
// ============================================================================

function getCurrentValueForStep(
  step: ActivationStepConfig,
  stats: ActivationStats
): number {
  switch (step.check) {
    case "totalWords":
      return stats.totalWords;
    case "learningWords":
      return stats.learningWords;
    case "reviewedCards":
      return stats.reviewedCards;
    case "knownWords":
      return stats.knownWords;
    case "phrases":
      return stats.phrases;
    case "openedFeed":
      return stats.feedOpened ? 1 : 0;
    case "completedLessons":
      return stats.completedLessons;
    case "listeningTime":
      return stats.listeningTimeSeconds;
  }
}

function isStepConditionMet(
  step: ActivationStepConfig,
  stats: ActivationStats
): boolean {
  return getCurrentValueForStep(step, stats) >= step.goal;
}

/**
 * Walk all steps in order and return the first step whose condition is NOT met.
 * If all conditions are met, returns ACTIVATION_COMPLETE_STEP (11).
 */
export function deriveCorrectStep(stats: ActivationStats): number {
  for (const step of ACTIVATION_STEPS) {
    if (!isStepConditionMet(step, stats)) {
      return step.step;
    }
  }
  return ACTIVATION_COMPLETE_STEP;
}

/**
 * Derive the activation baseline for existing users during rollout. This uses
 * durable stats only and intentionally skips the "openedFeed" step once the
 * feed unlock threshold is met because feed-open history was local-only before
 * this activation journey shipped.
 */
export function deriveExistingUserBaselineStep(stats: ActivationStats): number {
  for (const step of ACTIVATION_STEPS) {
    if (step.check === "openedFeed") {
      continue;
    }
    if (
      step.check === "reviewedCards" &&
      stats.reviewedCards < step.goal &&
      shouldRepairMissingReviewedCards(stats)
    ) {
      continue;
    }
    if (!isStepConditionMet(step, stats)) {
      return step.step;
    }
  }
  return ACTIVATION_COMPLETE_STEP;
}

export function deriveActivationRepairStep(
  storedStep: number,
  stats: ActivationStats
): number | undefined {
  if (!getStepConfig(storedStep)) {
    return undefined;
  }

  const correctStep = deriveCorrectStep(stats);
  if (correctStep > storedStep + 1) {
    return correctStep;
  }

  const existingUserBaselineStep = deriveExistingUserBaselineStep(stats);
  return existingUserBaselineStep > 3 && existingUserBaselineStep > storedStep + 1
    ? existingUserBaselineStep
    : undefined;
}

// ============================================================================
// Stats sourcing (PWA-specific — replaces iOS globalStore)
// ============================================================================

const ACTIVATION_STATS_QUERY_KEY = "activationStats";

/**
 * Pull the activation stats from the live react-query layer. Mirrors iOS
 * `applyServerStats` in globalStore.ts: same endpoints, same field mapping.
 */
function useActivationStats(): {
  stats: ActivationStats;
  isReady: boolean;
  refetch: () => void;
} {
  const { data, isSuccess, refetch } = useQuery({
    queryKey: [ACTIVATION_STATS_QUERY_KEY],
    queryFn: async () => {
      const [userStats, dictionaryStats] = await Promise.all([
        callWithAuth(getUserStats),
        callWithAuth(getDictionaryStats),
      ]);
      return { userStats, dictionaryStats };
    },
    staleTime: 60_000,
  });

  const refetchStats = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!data) {
    return {
      stats: { ...EMPTY_STATS, feedOpened: hasOpenedFeed() },
      isReady: false,
      refetch: refetchStats,
    };
  }

  const { userStats, dictionaryStats } = data;
  const knownWords = dictionaryStats?.knownWordCount ?? 0;
  const learningWords = dictionaryStats?.learningWordCount ?? 0;
  const phrases =
    (dictionaryStats?.knownPhraseCount ?? 0) +
    (dictionaryStats?.learningPhraseCount ?? 0);

  const stats: ActivationStats = {
    totalWords: knownWords + learningWords,
    learningWords,
    knownWords,
    reviewedCards: userStats?.reviewedCardsCount ?? 0,
    phrases,
    completedLessons: userStats?.completedLessonsCount ?? 0,
    listeningTimeSeconds: userStats?.listeningTime ?? 0,
    feedOpened: hasOpenedFeed(),
  };

  return { stats, isReady: isSuccess, refetch: refetchStats };
}

export interface ActivationJourneyState extends ActivationState {
  /** Server-synced `userSettings.activationStep` (1-based; 11 == complete). */
  currentStep: number;
  stepConfig: ActivationStepConfig | undefined;
  /** 0..1 progress toward the current step's goal. */
  currentProgress: number;
  isStepComplete: boolean;
  isActivationComplete: boolean;
  /** Live stats backing the derivation (handy for progress bars / debugging). */
  stats: ActivationStats;
  /**
   * True once the backing stats AND the stored `activationStep` have loaded, so
   * `currentStep` reflects the real server value rather than the pending `?? 1`
   * default. Consumers that react to step *transitions* (e.g. the module-complete
   * level-up cascade) must wait for this to avoid a spurious cold `1 → N` jump.
   */
  isReady: boolean;
  /** Persist advance to the next step (monotonic; no-op once complete). */
  advanceStep: () => Promise<void>;
  /**
   * Alias for `advanceStep`, matching the task's requested `completeStep`
   * ergonomics — "I finished the current step, move me on."
   */
  completeStep: () => Promise<void>;
}

/**
 * Core hook for the activation journey state machine.
 *
 * Reads `activationStep` from server-synced user settings and computes progress
 * toward the current goal using live stats from the query layer. Exposes
 * `advanceStep()` / `completeStep()` which persist the next step to the server,
 * and runs a repair check after stats load + on tab refocus: if the stored step
 * is behind what the user's actual stats support, it fast-forwards (never goes
 * backwards).
 */
export function useActivationJourney(): ActivationJourneyState {
  const { settings, updateSettings } = useUserSettings();
  const { stats, isReady, refetch: refetchStats } = useActivationStats();

  const currentStep = settings?.activationStep ?? 1;
  const stepConfig = getStepConfig(currentStep);
  const isActivationComplete = currentStep >= ACTIVATION_COMPLETE_STEP;

  let currentProgress = 0;
  let isStepComplete = false;
  if (stepConfig) {
    const current = getCurrentValueForStep(stepConfig, stats);
    currentProgress = Math.min(1, current / stepConfig.goal);
    isStepComplete = current >= stepConfig.goal;
  }

  const advanceStep = useCallback(async () => {
    if (isActivationComplete) return;
    await updateSettings({ activationStep: currentStep + 1 });
  }, [currentStep, isActivationComplete, updateSettings]);

  // Repair: after stats load + on tab refocus, fast-forward if stuck behind.
  // A single-step gap is the normal module-complete animation window; bigger
  // gaps mean the stored step missed prior advances and can safely catch up.
  // (iOS uses AppState 'active' and peeks the live store; the web equivalent is
  // window 'focus' reading the latest stats/step from refs updated each render.)
  const hasRepairedRef = useRef(false);
  const statsRef = useRef(stats);
  const storedStepRef = useRef(settings?.activationStep);
  const updateSettingsRef = useRef(updateSettings);
  const refetchStatsRef = useRef(refetchStats);
  statsRef.current = stats;
  storedStepRef.current = settings?.activationStep;
  updateSettingsRef.current = updateSettings;
  refetchStatsRef.current = refetchStats;

  const repair = useCallback(() => {
    const stored = storedStepRef.current ?? 1;
    const repairStep = deriveActivationRepairStep(stored, statsRef.current);
    if (repairStep !== undefined) {
      void updateSettingsRef.current({ activationStep: repairStep });
    }
  }, []);

  useEffect(() => {
    if (!isReady || settings?.activationStep == null) return;

    if (!hasRepairedRef.current) {
      hasRepairedRef.current = true;
      repair();
    }

    function handleFocus() {
      // Mirror iOS reading live store values on foreground: the global
      // queryClient disables refetchOnWindowFocus, so pull fresh stats here.
      // The query update re-renders this hook and re-runs the repair effect
      // below against the latest counts.
      refetchStatsRef.current();
      repair();
    }

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isReady, settings?.activationStep, repair]);

  // Re-run repair whenever the freshly fetched stats change, so a focus-driven
  // refetch fast-forwards a stuck step even though the refetch resolves
  // asynchronously after the focus handler fired.
  useEffect(() => {
    if (!isReady || settings?.activationStep == null) return;
    if (!hasRepairedRef.current) return;
    repair();
  }, [stats, isReady, settings?.activationStep, repair]);

  return {
    currentStep,
    stepConfig,
    currentProgress,
    isStepComplete,
    isActivationComplete,
    stats,
    isReady: isReady && settings?.activationStep != null,
    advanceStep,
    completeStep: advanceStep,
  };
}
